import { format } from 'date-fns';
import type { LocalDateInput, LocalDateString } from '../@types/localDate';

export type { LocalDateInput, LocalDateString } from '../@types/localDate';

const LOCAL_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Checks whether a value uses the `yyyy-MM-dd` format and represents an actual calendar date.
 */
function isValidLocalDateString(value: string): value is LocalDateString {
  if (!LOCAL_DATE_PATTERN.test(value)) {
    return false;
  }

  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);

  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

/**
 * Converts a Date or date-only string to a `yyyy-MM-dd` value in the local time zone.
 * Existing valid date-only strings are returned unchanged.
 *
 * @throws {RangeError} If the value cannot be converted to a valid calendar date.
 */
export function toLocalDateString(value: LocalDateInput): LocalDateString {
  if (typeof value === 'string') {
    if (isValidLocalDateString(value)) {
      return value;
    }

    throw new RangeError(`Invalid local date value: ${value}`);
  }

  if (Number.isNaN(value.getTime())) {
    throw new RangeError(`Invalid local date value: ${value}`);
  }

  return format(value, 'yyyy-MM-dd') as LocalDateString;
}

type SerializedLocalDate<T> = T extends null | undefined ? T : LocalDateString;

export type WithLocalDateStrings<T, K extends keyof T> = Omit<T, K> & {
  [P in K]: SerializedLocalDate<T[P]>;
};

/**
 * Returns a shallow copy of an object with the selected date-only fields serialized as
 * `yyyy-MM-dd`. Null, undefined, empty, and unselected fields remain unchanged.
 *
 * @throws {RangeError} If a selected non-empty date value is invalid.
 */
export function serializeLocalDateFields<T extends Record<string, unknown>, K extends keyof T>(
  value: T,
  fields: readonly K[]
): WithLocalDateStrings<T, K> {
  const serialized = { ...value };

  fields.forEach((field) => {
    const fieldValue = value[field];

    if (fieldValue instanceof Date || (typeof fieldValue === 'string' && fieldValue !== '')) {
      serialized[field] = toLocalDateString(fieldValue as LocalDateInput) as T[K];
    }
  });

  return serialized as WithLocalDateStrings<T, K>;
}
